/**
 * Slide-in lead magnet popup driving downloads of the 7-Stage Business Jumpstart Blueprint.
 *
 * Triggers:
 *   - Scroll past 50% of page, OR
 *   - 25 seconds on page, OR
 *   - Desktop exit-intent (mouse leaves through top of viewport)
 * Whichever fires first.
 *
 * Suppressed if:
 *   - User has already dismissed in this browser session
 *   - User is on an admin or portal route
 *   - The trigger fires within 8s of pageload (avoid intrusive instant pop)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { X, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { markResourceDownloaded, hasDownloadedResource, lookupSuppressionByEmail } from '../lib/leadMagnetSuppression';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'dca_jumpstart_popup_dismissed';
const SHOW_COUNT_KEY = 'dca_jumpstart_popup_shows';

export default function SlideInLeadMagnet() {
  const location = useLocation();
  const settings = useSiteSettings();
  const cfg = (settings && settings.popup) || {};
  const enabled = cfg.enabled !== false;
  const headline = cfg.headline || 'Get the 7-Stage Business Jumpstart Blueprint';
  const subHeadline = cfg.sub_headline || 'A practical roadmap from idea to a credible, launch-ready business.';
  const ctaLabel = cfg.cta_label || 'Send Me the Blueprint';
  const resourceId = cfg.resource_id || 'res_jumpstart_checklist';
  const resourceName = cfg.resource_name || '7-Stage Business Jumpstart Blueprint';
  const delaySec = Number(cfg.trigger_delay_seconds || 25);
  const scrollPct = Number(cfg.trigger_scroll_percent || 50);
  const exitEnabled = cfg.exit_intent_enabled !== false;
  const exitStyle = cfg.exit_intent_style || 'center_modal'; // 'center_modal' | 'slide_in'
  const maxShows = Number(cfg.max_shows_per_session || 1);
  const suppressMobile = !!cfg.suppress_on_mobile;
  const minDelayMs = Math.max(0, Number(cfg.min_delay_seconds || 8)) * 1000;

  const [open, setOpen] = useState(false);
  const [centerModal, setCenterModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });

  const onAdminOrPortal = /^\/(admin|portal|login|signup|client|free-health-jumpstart|free-life-jumpstart|challenge)/i.test(location.pathname);

  const dismiss = useCallback(() => {
    setOpen(false);
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* private mode */ }
  }, []);

  useEffect(() => {
    if (!enabled || onAdminOrPortal) return;
    if (suppressMobile && window.innerWidth < 768) return;
    // Persistent suppression: visitor already downloaded this lead magnet on
    // this browser - never show the popup again. Survives across sessions.
    if (hasDownloadedResource(resourceId)) return;

    let shows = 0;
    try { shows = Number(sessionStorage.getItem(SHOW_COUNT_KEY) || 0); } catch (e) { /* fall through */ }
    if (shows >= maxShows) return;
    try { if (sessionStorage.getItem(SESSION_KEY)) return; } catch (e) { /* fall through */ }

    const startedAt = Date.now();
    let opened = false;
    const trigger = (asCenter = false) => {
      if (opened) return;
      if (Date.now() - startedAt < minDelayMs) return;
      opened = true;
      setCenterModal(asCenter);
      setOpen(true);
      try {
        sessionStorage.setItem(SHOW_COUNT_KEY, String(shows + 1));
      } catch (e) { /* ignore */ }
    };

    const timeTimer = setTimeout(() => trigger(false), delaySec * 1000);

    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total > 0 && (scrolled / total) * 100 >= scrollPct) trigger(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    const onLeave = (e) => {
      if (!exitEnabled) return;
      if (e.clientY < 5 && window.innerWidth >= 768) trigger(exitStyle === 'center_modal');
    };
    document.addEventListener('mouseleave', onLeave);

    return () => {
      clearTimeout(timeTimer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseleave', onLeave);
    };
  }, [enabled, onAdminOrPortal, suppressMobile, maxShows, minDelayMs, delaySec, scrollPct, exitEnabled, exitStyle, resourceId, location.pathname]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error('First name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/resources/download`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || form.first_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: (form.phone || '').trim(),
        resource_id: resourceId,
        resource_name: resourceName,
        lead_source: centerModal ? 'popup_exit_intent' : 'popup_blueprint',
        page_submitted_from: location.pathname,
      });
      setDone(true);
      toast.success('Check your inbox - the Blueprint is on its way.');
      markResourceDownloaded(resourceId, form.email);
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e2) { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send the Blueprint. Try again or visit /resources.');
    } finally {
      setSubmitting(false);
    }
  };

  // "Already grabbed this?" inline check.  Fires when the visitor finishes
  // typing/leaves the email field.  Looks them up by email server-side and,
  // if they've previously converted on this resource (even on another
  // device), gracefully closes the popup with a friendly "you already have
  // it" message instead of asking them to convert again.
  const checkEmailAlreadyConverted = async () => {
    const e = (form.email || '').trim().toLowerCase();
    if (!e || !e.includes('@') || e.length < 6) return;
    await lookupSuppressionByEmail(e);
    if (hasDownloadedResource(resourceId)) {
      setDone(true);  // flip popup to success state
      toast.success("Looks like you already grabbed this - check your inbox.");
    }
  };

  if (!open || onAdminOrPortal || !enabled) return null;

  // Center modal (exit-intent on desktop)
  if (centerModal) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" data-testid="exit-modal" role="dialog" aria-modal="true">
        <div className="bg-white shadow-2xl rounded-2xl w-full max-w-md overflow-hidden">
          <div className="h-1.5 bg-[#1D4ED8]" />
          <div className="px-6 pt-6 pb-6 relative">
            <button onClick={dismiss} aria-label="Close" data-testid="exit-modal-close" className="absolute top-3 right-3 text-[#94A3B8] hover:text-[#0A0F1C]"><X size={20} /></button>
            {!done ? (
              <>
                <div className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#F97316] mb-2">Wait! Before you go</div>
                <h3 className="font-bold text-[#0A0F1C] text-xl leading-snug mb-2">{headline}</h3>
                <p className="text-sm text-[#475569] leading-relaxed mb-4">{subHeadline}</p>
                <form onSubmit={submit} className="space-y-2.5" data-testid="exit-modal-form">
                  <div className="grid grid-cols-2 gap-2">
                    <input required type="text" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="exit-modal-first" />
                    <input type="text" placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="exit-modal-last" />
                  </div>
                  <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} onBlur={checkEmailAlreadyConverted} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="exit-modal-email" />
                  <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="exit-modal-phone" />
                  <button type="submit" disabled={submitting} className="w-full bg-[#F97316] hover:bg-[#FB923C] text-white font-bold text-sm py-3 rounded-md flex items-center justify-center gap-2 disabled:opacity-60" data-testid="exit-modal-submit">
                    {submitting ? 'Sending...' : (<>{ctaLabel} <ArrowRight size={14} /></>)}
                  </button>
                  <p className="text-[10px] text-[#94A3B8] text-center">No spam. Unsubscribe anytime.</p>
                </form>
              </>
            ) : (
              <div className="text-center py-3" data-testid="exit-modal-success">
                <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-2" />
                <div className="font-bold text-[#0A0F1C] text-lg mb-1">You are all set</div>
                <p className="text-sm text-[#475569] mb-3">The Blueprint is on its way to your inbox.</p>
                <button onClick={dismiss} className="text-sm text-[#1D4ED8] font-bold hover:underline">Close</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Standard slide-in
  return (
    <div className="fixed inset-x-0 bottom-[64px] sm:bottom-6 sm:inset-auto sm:right-6 z-[80] animate-fade-in-up" data-testid="slide-in-blueprint" role="dialog" aria-modal="true">
      <div className="bg-white shadow-2xl border border-[#E2E8F0] sm:rounded-2xl rounded-t-2xl sm:w-[400px] w-full overflow-hidden">
        <div className="h-1.5 bg-[#1D4ED8]" />
        <div className="px-5 pt-5 pb-5 relative">
          <button onClick={dismiss} aria-label="Close" data-testid="slide-in-close" className="absolute top-3 right-3 text-[#94A3B8] hover:text-[#0A0F1C]"><X size={18} /></button>
          {!done ? (
            <>
              <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#F97316] mb-1">FREE Download</div>
              <h3 className="font-bold text-[#0A0F1C] text-lg leading-snug mb-1">{headline}</h3>
              <p className="text-xs text-[#475569] leading-relaxed mb-3">{subHeadline}</p>
              <form onSubmit={submit} className="space-y-2" data-testid="slide-in-form">
                <div className="grid grid-cols-2 gap-2">
                  <input required type="text" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="slide-in-first-name" />
                  <input type="text" placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="slide-in-last-name" />
                </div>
                <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} onBlur={checkEmailAlreadyConverted} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="slide-in-email" />
                <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="slide-in-phone" />
                <button type="submit" disabled={submitting} className="w-full bg-[#F97316] hover:bg-[#FB923C] text-white font-bold text-sm py-2.5 rounded-md flex items-center justify-center gap-2 disabled:opacity-60" data-testid="slide-in-submit">
                  {submitting ? 'Sending...' : (<>{ctaLabel} <ArrowRight size={14} /></>)}
                </button>
                <p className="text-[10px] text-[#94A3B8] text-center pt-1">No spam. Unsubscribe anytime.</p>
              </form>
            </>
          ) : (
            <div className="text-center py-3" data-testid="slide-in-success">
              <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-2" />
              <div className="font-bold text-[#0A0F1C] text-base mb-1">You are all set</div>
              <p className="text-xs text-[#475569] mb-3">The Blueprint is on its way to your inbox.</p>
              <button onClick={dismiss} className="text-xs text-[#1D4ED8] font-bold hover:underline">Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
